import { createRootRoute, Outlet } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider, type Query } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createIdbPersister } from '@/lib/idbPersister'
import { desktop } from '@/lib/printTransport'
import { OfflineIndicator } from '@/components/OfflineIndicator'
import { DesktopBranding } from '@/components/DesktopBranding'
import { DesktopMinimizeButton } from '@/components/DesktopMinimizeButton'
import { DesktopUpdateBanner } from '@/components/DesktopUpdateBanner'
import '../index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

// Only slow-changing REFERENCE data is persisted for offline use — never
// transactional/live data (orders, payments, dashboard, reports), which would
// be misleading if shown stale. The Electron shell clears all storage on a
// tenant re-point, so the cache can't bleed across restaurants.
const PERSIST_PREFIXES = new Set([
  'settings',
  'categories',
  'admin-categories',
  'products',
  'admin-products',
  'menu',
  'stations',
  'kitchen-stations',
  'tables',
])

function shouldPersistQuery(query: Query): boolean {
  const first = query.queryKey?.[0]
  return typeof first === 'string' && PERSIST_PREFIXES.has(first)
}

// IndexedDB persister — the menu snapshot (products carry multi-MB base64
// image_urls) blows localStorage's ~5 MB quota, so we persist to IndexedDB.
// Persistence is activated ONLY inside the desktop app (see component below),
// so the live web tenants keep exactly today's in-memory behaviour.
const persister =
  typeof window !== 'undefined' ? createIdbPersister('bhookly-rq-cache') : null

export const Route = createRootRoute({
  component: () => {
    const inner = (
      <>
        <div className="min-h-screen bg-background">
          <Outlet />
        </div>
        <OfflineIndicator />
        <DesktopBranding />
        <DesktopUpdateBanner />
        <DesktopMinimizeButton />
        <ReactQueryDevtools initialIsOpen={false} />
      </>
    )

    // Desktop app: persist reference reads so the UI is usable offline.
    if (persister && desktop()) {
      return (
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister,
            maxAge: 1000 * 60 * 60 * 24, // keep cached reads for 24h
            dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
          }}
        >
          {inner}
        </PersistQueryClientProvider>
      )
    }

    // Browser (all live web tenants): unchanged in-memory client.
    return <QueryClientProvider client={queryClient}>{inner}</QueryClientProvider>
  },
})
